import { images } from "@img/imagesMap";
import ButtonRosterVersion from "./ButtonRosterVersion";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import ModifiedCellsContext from "@/pages/roster-create/context/ModifiedCellsContext";
import { useScheduleStore } from "@store/scheduleStore";
import { useOutsideClick } from "@script/script";
import RosterCreateModal from "@pages/roster-create/components/roster-create-modal.tsx";
import BtnCreateRoster from "@components/common/BtnCreateRoster.tsx";
import CustomModal from "@/components/modal/CustomModal";
import useSaveRosterMutation from "@hooks/roster-craete/useSaveRosterMutation";
import useDeleteRosterMutation from "@hooks/roster-craete/useDeleteRosterMutation";
import ButtonInitial from "@components/ui/button/btn-initial.tsx";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@components/ui/tooltip.tsx";
import { cn } from "@utils/cn.ts";
import useStore from "@/store/store";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import { useIsAdmin } from "@hooks/useAuth.ts";
import { useIsHeadNurse } from "@/hooks/useHeadNurse";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import WantedSelectorModal from "./components/WantedSelectorModal";
import { toast } from "@/components/toast/CustomToast";
import useRosterVersions from "@/hooks/roster-craete/useRosterVersions";
import httpClient from "@/config/axios";
import useThrottle from "@/hooks/useThrottle";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import type { RosterResponse } from "@/types/snapshot-type";
import { useShiftsQuery } from "@/hooks/roster-craete/useShiftsQuery";
import ModalEmergencyReplacement from "./ModalEmergencyReplacement";
import type { CopyNurses } from "@utils/rosterCreate-util.ts";
import type {
  EmergencyBulkPeriodPick,
  EmergencyReplacementSlotPick,
} from "@/types/emergency-replacement.types";
import type * as type from "@vTypes";
import { useRosterCreateUiStore } from "@/store/rosterCreateUiStore";
import { Button } from "@/components/ui/button";

const shouldUseAsyncRosterGeneration = import.meta.env.VITE_ENV !== "LOCAL";

/** 발행/마감철회 mutate — Roster_create useMutation과 동일 시그니처 */
type PublishMutateFn = (
  variables?: void,
  options?: {
    onSuccess?: () => void;
    onError?: (error: Error) => void;
    onSettled?: () => void;
  }
) => void;

export type ButtonGroupProps = {
  /** 미지정 시 `rosterCreateUiStore`의 edit 모드 토글 */
  setIsEditMode?: React.Dispatch<React.SetStateAction<boolean>>;
  /** 근무표 수정 모달을 '수정' 버튼 바로 아래에 맞출 때 사용 */
  editModeButtonRef?: React.RefObject<HTMLButtonElement | null>;
  memo: string;
  copyNurse: CopyNurses;
  generateRoster: (args: {
    gradeStrategy: string;
  }) => Promise<{ schedule_id?: string; job_id?: string } | null | undefined>;
  isPublishPending: boolean;
  publishRoster: PublishMutateFn;
  isPublished: boolean;
  unPublishRoster: PublishMutateFn;
  isUnPublishPending: boolean;
  scheduleDownload_excel: () => void | Promise<void>;
  isRosterCreating: boolean;
  isChangeDateButtonClick: boolean;
  setIsChangeDateButtonClick: React.Dispatch<React.SetStateAction<boolean>>;
  currentTeamTableViewTab: number;
  selectedGroupId?: string | null;
  isRosterEmpty?: boolean;
  /** 원티드 본 모달 — 미지정 시 store `wanted` */
  setIsOpendModalSubmittedWantedlistModal?: (value: boolean) => void;
  /** 원티드 선택 모달 — 미지정 시 store `wantedSelector` */
  isOpendWantedSelectorModal?: boolean;
  setIsOpendWantedSelectorModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setWantedModalInitialTab?: (tab: "view" | "settings") => void;
  /** 원티드 반영사항 로딩 중이면 일부 버튼 비활성화 */
  isWantedAdjustmentLoading?: boolean;
  clearHistory?: () => void;
  isEmergencyModalOpen?: boolean;
  setIsEmergencyModalOpen?: React.Dispatch<React.SetStateAction<boolean>>;
  emergencySlotPick: EmergencyReplacementSlotPick | null;
  setEmergencySlotPick: React.Dispatch<
    React.SetStateAction<EmergencyReplacementSlotPick | null>
  >;
  emergencyBulkPeriodPick?: EmergencyBulkPeriodPick | null;
  setEmergencyBulkPeriodPick?: React.Dispatch<
    React.SetStateAction<EmergencyBulkPeriodPick | null>
  >;
  /** 모달 내부 당일/기간 라디오 변경 시 부모 상태 동기화 */
  onEmergencyReplacementPeriodModeChange?: (next: "sameDay" | "period") => void;
  /** BULK: 시나리오 선택 시 steps 동기화 (테이블 하이라이트) */
  onEmergencyBulkPathStepsChange?: (
    steps: Array<{ date: string; nurseId: string }> | null
  ) => void;
  /** SINGLE: 추천 결과 수신/초기화 시 부모에 알림 (뒤 테이블 셀 클릭 차단 판단용) */
  onEmergencySingleResultChange?: (hasResult: boolean) => void;
  /** BULK: 추천 결과 수신/초기화 시 부모에 알림 (뒤 테이블 셀 클릭 차단 판단용) */
  onEmergencyBulkResultChange?: (hasResult: boolean) => void;
  /** 뒤 테이블 confirm 승인 시 모달 결과 초기화 함수를 등록할 ref */
  emergencyBulkResultResetRef?: React.MutableRefObject<(() => void) | null>;
  /** 모달 추천 선택 → 테이블 하이라이트 동기화 및 닫을 때 초기화 */
  setEmergencyReplacementCandidateNurseId?: React.Dispatch<
    React.SetStateAction<string | null>
  >;
  onApplyEmergencyReplacement?: (args: {
    nurseId: string;
    targetNurseId: string;
    dayIndex: number;
    shiftCode: string;
  }) => void;
  /** 긴급대체 모달 시프트 PK→표시명 매핑 */
  availableShifts?: type.availableShifts[];
  /**
   * 긴급대체 모달 open 변경. 전달 시 `onOpenChange`에 사용 (닫힐 때 slotPick 지연 등).
   * 미전달 시: 닫을 때 즉시 `setEmergencySlotPick(null)`.
   */
  onEmergencyModalOpenChange?: (open: boolean) => void;
  /**
   * 긴급대체 버튼 클릭. 미전달 시: slotPick 초기화 후 모달 오픈.
   */
  openEmergencyModalFromButton?: () => void;
  /**
   * 수정 버튼 클릭. 미전달 시: 수정 모드 토글.
   */
  openEditModeFromButton?: () => void;
};

export default function ButtonGroup({
  setIsEditMode,
  editModeButtonRef,
  memo,
  copyNurse,
  isPublishPending,
  publishRoster,
  isPublished,
  unPublishRoster,
  isUnPublishPending,
  generateRoster,
  scheduleDownload_excel,
  isRosterCreating,
  isChangeDateButtonClick,
  setIsChangeDateButtonClick,
  currentTeamTableViewTab,
  selectedGroupId,
  isRosterEmpty,
  setIsOpendModalSubmittedWantedlistModal,
  isOpendWantedSelectorModal,
  setIsOpendWantedSelectorModal,
  setWantedModalInitialTab,
  isWantedAdjustmentLoading = false, // 원티드 반영사항 로딩 중일 때 버튼 비활성화
  clearHistory,
  /** true면 전체보기/팀별보기 토글을 렌더하지 않음 (RosterViewSettings 등으로 이동) */
  isEmergencyModalOpen,
  setIsEmergencyModalOpen,
  emergencySlotPick,
  setEmergencySlotPick,
  emergencyBulkPeriodPick,
  setEmergencyBulkPeriodPick,
  onEmergencyReplacementPeriodModeChange,
  onEmergencyBulkPathStepsChange,
  onEmergencySingleResultChange,
  onEmergencyBulkResultChange,
  emergencyBulkResultResetRef,
  setEmergencyReplacementCandidateNurseId,
  onApplyEmergencyReplacement,
  availableShifts = [],
  onEmergencyModalOpenChange,
  openEmergencyModalFromButton,
  openEditModeFromButton,
}: ButtonGroupProps) {
  const uiMode = useRosterCreateUiStore((state) => state.mode);

  const { modifiedCells } = useContext(ModifiedCellsContext);
  const hasUnsavedChanges = modifiedCells.size > 0;
  const setUiMode = useRosterCreateUiStore((state) => state.setMode);
  const [modalOpen, setModalOpen] = useState(false); // Modal
  const [confirmModal, setConfirmMOdal] = useState(false);
  const [publishModal, setPublishModal] = useState(false);
  /** 모달 오픈 시점 의도 — 성공 후 쿼리 갱신으로 isPublished가 바뀌어도 제목이 안 바뀜 */
  const [publishModalAction, setPublishModalAction] = useState<
    "publish" | "unpublish" | null
  >(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [gradeStrategy, setGradeStrategy] = useState<string>("COMBINED");
  const isAdmin = useIsAdmin();
  const isHeadNurse = useIsHeadNurse();
  const isEmergencyOpen = isEmergencyModalOpen ?? uiMode === "emergency";
  const setEmergencyOpen: React.Dispatch<React.SetStateAction<boolean>> =
    setIsEmergencyModalOpen ??
    ((next) => {
      const open =
        typeof next === "function" ? next(uiMode === "emergency") : next;
      setUiMode(open ? "emergency" : "none");
    });

  const isWantedSelectorOpen =
    isOpendWantedSelectorModal !== undefined
      ? isOpendWantedSelectorModal
      : uiMode === "wantedSelector";

  const queryClient = useQueryClient();
  const throttle = useThrottle(3000);
  const isJustCreatedRef = useRef<boolean>(false);

  const groupId = isAdmin ? (selectedGroupId ?? "") : "";

  const [isOpendChangeDateList, setIsOpendChangeDateList] =
    useState<boolean>(false);

  const refDateList = useRef<HTMLDivElement | null>(null);
  const refDateListScroll = useRef<HTMLDivElement | null>(null);
  const refEmergencyReplacementButton = useRef<HTMLButtonElement | null>(null);

  /** 모달에 인라인 함수를 넘기면 매 렌더마다 참조가 바뀌어 긴급대체 시나리오 선택 시 useEffect 무한루프(페이지 멈춤) 발생 */
  const handleBulkPathStepsChange = useCallback(
    (
      steps: Array<{
        date: string;
        shift: string;
        candidate: { nurse_id?: string; name?: string };
      }> | null
    ) => {
      onEmergencyBulkPathStepsChange?.(
        steps
          ? steps.map((s) => ({
              date: s.date,
              nurseId: String(s.candidate?.nurse_id ?? ""),
            }))
          : null
      );
    },
    [onEmergencyBulkPathStepsChange]
  );

  const handleEmergencyReplacementPeriodModeChange = useCallback(
    (next: "sameDay" | "period", previous: "sameDay" | "period") => {
      if (previous === "sameDay" && next === "period") {
        setEmergencySlotPick(null);
      }
      if (previous === "period" && next === "sameDay") {
        setEmergencyBulkPeriodPick?.(null);
      }
      onEmergencyReplacementPeriodModeChange?.(next);
    },
    [
      setEmergencySlotPick,
      setEmergencyBulkPeriodPick,
      onEmergencyReplacementPeriodModeChange,
    ]
  );

  /** BULK 스트립: 테이블과 동일 — `copyNurse` 행의 schedule + schedule_ids */
  const emergencyBulkPeriodTargetNurse = useMemo(() => {
    const nid = emergencyBulkPeriodPick?.nurseId;
    if (!nid || !copyNurse?.length) return null;
    const row = copyNurse.find(
      (n) =>
        String(
          (n as { nurse_id?: string }).nurse_id ?? (n as { id?: string }).id
        ) === String(nid)
    );
    if (!row?.schedule?.length) return null;
    const schedule_ids = (row as { schedule_ids?: (number | null)[] | null })
      .schedule_ids;
    return { schedule: row.schedule, schedule_ids };
  }, [emergencyBulkPeriodPick?.nurseId, copyNurse]);

  const { curDate, curScheduleInfo, setCurDate, setCurScheduleInfo } =
    useScheduleStore();

  // 원티드 상태는 ButtonGroup에서 직접 가져오기 (먼저 체크)
  const gid = isAdmin ? (selectedGroupId ?? null) : "";

  const { data: wantedStatus, isLoading: isLoadingWantedStatus } =
    useWantedStatusQuery({
      year: curDate?.year,
      month: curDate?.month,
      groupId: gid,
    });

  // 원티드가 있고 버전 리스트를 가져올 수 있는지 확인
  const hasWanted = wantedStatus && wantedStatus.status !== null;

  const { data: shiftsRes } = useShiftsQuery(
    isAdmin ? (selectedGroupId ?? "") : ""
  );

  // 모달 오픈 시 스크롤 잠금 (c_main__container가 스크롤 영역임)
  useEffect(() => {
    const scrollContainer = document.querySelector(
      ".c_main__container"
    ) as HTMLElement;
    const target = scrollContainer || document.body;

    if (modalOpen || confirmModal || publishModal || deleteModal) {
      target.style.overflow = "hidden";
    } else {
      target.style.overflow = "auto";
    }
    return () => {
      target.style.overflow = "auto";
    };
  }, [modalOpen, confirmModal, publishModal, deleteModal]);

  function changeDate(inc: number) {
    setIsChangeDateButtonClick(true);
    const date = new Date(curDate?.year ?? 0, (curDate?.month ?? 0) - 1 + inc);
    // 날짜만 변경 (useRosterVersions가 자동으로 버전 리스트를 가져옴)
    setCurDate({ year: date.getFullYear(), month: date.getMonth() + 1 });
  }

  /* 외부 클릭 */
  useOutsideClick(refDateList, () => setIsOpendChangeDateList(false));

  const { mutate: saveRosterMutate, isPending: isSaving } =
    useSaveRosterMutation();
  const { mutate: deleteRosterMutate, isPending: isDeleting } =
    useDeleteRosterMutation();

  // 버전 리스트 가져오기 (isJustCreatedRef 처리용)
  const { data: versionList } = useRosterVersions({
    groupId: isAdmin ? (selectedGroupId ?? null) : null,
  });

  // 빈 근무표 생성
  const { mutate: createEmptyRosterMutate } = useMutation({
    mutationFn: async () => {
      const params = new URLSearchParams({
        year: String(curDate?.year),
        month: String(curDate?.month),
      });

      const gid = isAdmin ? (selectedGroupId ?? undefined) : undefined;
      if (gid) {
        params.append("group_id", gid);
      }

      return await httpClient.post(
        `/roster/create-with-weekly-off?${params.toString()}`
      );
    },
    onSuccess: async () => {
      toast.success("새 근무표가 생성되었습니다.");
      isJustCreatedRef.current = true;

      // 버전 목록 쿼리를 무효화하여 다시 가져오도록 함
      // useRosterVersions의 queryKey와 일치하도록 groupId를 null로 통일
      const groupIdForQuery = isAdmin ? (selectedGroupId ?? null) : null;
      await queryClient.invalidateQueries({
        queryKey: ["VERSIONS", curDate?.year, curDate?.month, groupIdForQuery],
      });
    },
  });

  // 근무표 복사
  const { mutate: copyRosterMutate } = useMutation({
    mutationFn: async () => {
      if (!curScheduleInfo?.scheduleId) {
        throw new Error("복사할 근무표가 없습니다.");
      }
      return await httpClient.post(
        `/roster/copy/${curScheduleInfo.scheduleId}`
      );
    },
    onSuccess: async () => {
      toast.success("근무표가 복사되었습니다.");
      isJustCreatedRef.current = true;

      // 버전 목록 쿼리를 무효화하여 다시 가져오도록 함
      // useRosterVersions의 queryKey와 일치하도록 groupId를 null로 통일
      const groupIdForQuery = isAdmin ? (selectedGroupId ?? null) : null;
      await queryClient.invalidateQueries({
        queryKey: ["VERSIONS", curDate?.year, curDate?.month, groupIdForQuery],
      });
    },
  });

  // isJustCreatedRef 처리: versionList가 업데이트되면 첫 번째 버전 선택
  useEffect(() => {
    if (!versionList || versionList.length === 0) return;

    if (isJustCreatedRef.current) {
      const latestVersion = versionList[0];
      setCurScheduleInfo({
        scheduleId: latestVersion.schedule_id,
        version: latestVersion.version,
        status: latestVersion.status,
      });
      isJustCreatedRef.current = false;
    }
  }, [versionList, setCurScheduleInfo]);

  const handleCreateEmptyRoster = () => {
    throttle(() => {
      createEmptyRosterMutate();
    });
  };

  const handleCopyRoster = () => {
    throttle(() => {
      if (!curScheduleInfo?.scheduleId) {
        toast.error("복사할 근무표가 없습니다.");
        return;
      }
      if (confirm("현재 근무표를 복사하시겠습니까?")) {
        copyRosterMutate();
      }
    });
  };

  function saveRosterChanges() {
    const gid = isAdmin && selectedGroupId ? selectedGroupId : undefined;

    if (
      !curDate?.year ||
      !curDate?.month ||
      !selectedGroupId ||
      !curScheduleInfo?.scheduleId
    ) {
      toast.error("필수 정보가 누락되었습니다.");
      return;
    }

    saveRosterMutate(
      {
        data: {
          year: curDate.year,
          month: curDate.month,
          /* API/뮤테이션 타입은 스냅샷 Roster, 실제로는 CopyNurses(편집 행) 전달 */
          roster: copyNurse as unknown as RosterResponse["roster"],
          schedule_id: curScheduleInfo.scheduleId,
          memo: memo,
        },
        group_id: gid,
      },
      {
        onSuccess: async () => {
          setConfirmMOdal(false);
          clearHistory?.();
          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.nurses.list(groupId),
          });
          // 스케줄 데이터 캐시 무효화

          await queryClient.invalidateQueries({
            queryKey: QUERY_KEYS.roster.schedule(
              curScheduleInfo?.scheduleId ?? "",
              groupId
            ),
          });
        },
      }
    );
  }

  const publishing = () => {
    publishRoster(undefined, {
      onSuccess: () => {
        setPublishModal(false);
        setPublishModalAction(null);
      },
    });
  };

  const unPublishing = () => {
    unPublishRoster(undefined, {
      onSuccess: () => {
        setPublishModal(false);
        setPublishModalAction(null);
      },
    });
  };

  const deleting = () => {
    if (!curScheduleInfo?.scheduleId) {
      alert("삭제할 근무표가 없습니다.");
      return;
    }
    deleteRosterMutate(curScheduleInfo.scheduleId, {
      onSuccess: () => {
        setDeleteModal(false);
      },
    });
  };

  // 날짜 리스트 생성 로직 수정
  const dateListOptions = useMemo(() => {
    const options = [];
    const todayYear = new Date().getFullYear();
    const startYear = todayYear + 1;
    const endYear = todayYear - 1;

    for (let y = startYear; y >= endYear; y--) {
      for (let month = 12; month >= 1; month--) {
        options.push({ year: y, month });
      }
    }
    return options;
  }, []);

  // Select Box 열었을때 중간에 위치
  useEffect(() => {
    if (isOpendChangeDateList && refDateListScroll.current) {
      const currentElement =
        refDateListScroll.current.querySelector(".current");
      if (currentElement) {
        currentElement.scrollIntoView({ block: "center", behavior: "auto" });
      }
    }
  }, [isOpendChangeDateList]);

  return (
    <>
      {/* 근무표 생성 - 모달 */}
      <RosterCreateModal
        title={"근무표 생성시 우선 적용사항을 선택하세요."}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        isPending={isRosterCreating}
        pendingMsg={"근무표를 생성 중입니다."}
        gradeStrategy={gradeStrategy}
        setGradeStrategy={setGradeStrategy}
      >
        <BtnCreateRoster
          onClick={async () => {
            setModalOpen(false);
            sessionStorage.removeItem("roster_creation_reload_lock");

            try {
              await generateRoster({ gradeStrategy });
            } catch (error) {
              if (
                !(error instanceof Error) ||
                error.name !== "RosterInfeasibleError"
              ) {
                toast.error(
                  error instanceof Error
                    ? error.message
                    : "근무표 생성 중 오류가 발생했습니다."
                );
              }
              return;
            }

            if (shouldUseAsyncRosterGeneration) {
              useStore.getState().startPolling();
            } else {
              useStore.getState().resetRosterMadeState();
            }
          }}
          disabled={
            isAdmin || isRosterCreating || !hasWanted
          }
          isCreating={isRosterCreating}
          pageErrorCase={hasWanted ? "prepared" : "notRequestedWanted"}
        />
      </RosterCreateModal>
      {/* 근무표 저장 */}
      <CustomModal.Confirm
        isOpen={confirmModal}
        onConfirm={saveRosterChanges}
        title="근무표를 저장하시겠습니까?"
        pendingMsg={"근무표를 저장중입니다."}
        onClose={() => setConfirmMOdal(false)}
        isPending={isSaving}
      />
      {/* 근무표 발행 */}
      <CustomModal.Confirm
        isOpen={publishModal}
        onConfirm={
          publishModalAction === "unpublish" ? unPublishing : publishing
        }
        title={
          publishModalAction === "unpublish"
            ? "마감을 철회하시겠습니까?"
            : "근무표를 발행하시겠습니까?"
        }
        pendingMsg={
          publishModalAction === "unpublish"
            ? "마감을 철회중입니다.."
            : "근무표를 발행중입니다.."
        }
        onClose={() => {
          setPublishModal(false);
          setPublishModalAction(null);
        }}
        isPending={
          publishModalAction === "unpublish"
            ? isUnPublishPending
            : isPublishPending
        }
      />
      {/* 근무표 삭제 */}
      <CustomModal.Confirm
        isOpen={deleteModal}
        onConfirm={deleting}
        title={"근무표를 삭제하시겠습니까?"}
        pendingMsg={"근무표를 삭제중입니다."}
        onClose={() => setDeleteModal(false)}
        isPending={isDeleting}
      />
      {/* Watned Selector Modal - 이거 상태 정리좀해야함..*/}
      <WantedSelectorModal
        selectedGroupId={selectedGroupId}
        isOpen={isWantedSelectorOpen}
        onClose={() => {
          setIsOpendWantedSelectorModal?.(false);
          if (isOpendWantedSelectorModal === undefined) setUiMode("none");
        }}
        onViewStatus={() => {
          setWantedModalInitialTab?.("view");
          if (setIsOpendModalSubmittedWantedlistModal) {
            setIsOpendWantedSelectorModal?.(false);
            setIsOpendModalSubmittedWantedlistModal(true);
          } else {
            setUiMode("wanted");
          }
        }}
        onSettings={() => {
          setWantedModalInitialTab?.("settings");
          if (setIsOpendModalSubmittedWantedlistModal) {
            setIsOpendWantedSelectorModal?.(false);
            setIsOpendModalSubmittedWantedlistModal(true);
          } else {
            setUiMode("wanted");
          }
        }}
      />

      {/* 근무자 긴급대체 */}
      <ModalEmergencyReplacement
        open={isEmergencyOpen}
        availableShifts={availableShifts}
        onOpenChange={
          onEmergencyModalOpenChange ??
          ((next) => {
            setEmergencyOpen(next);
            if (!next) {
              setEmergencySlotPick(null);
              setEmergencyReplacementCandidateNurseId?.(null);
            }
          })
        }
        targetRef={refEmergencyReplacementButton}
        hasWorkers={copyNurse.length > 0}
        slotPick={emergencySlotPick}
        scheduleId={curScheduleInfo?.scheduleId}
        groupId={isAdmin ? (selectedGroupId ?? undefined) : undefined}
        shiftDefinitions={shiftsRes}
        onApplyReplacement={onApplyEmergencyReplacement}
        onReplacementCandidateChange={setEmergencyReplacementCandidateNurseId}
        bulkPeriodPick={emergencyBulkPeriodPick ?? null}
        bulkPeriodTargetNurse={emergencyBulkPeriodTargetNurse}
        bulkPeriodCalendar={
          curDate?.year != null && curDate?.month != null
            ? { year: curDate.year, month: curDate.month }
            : null
        }
        onBulkPathStepsChange={handleBulkPathStepsChange}
        onRecommendResultChange={onEmergencySingleResultChange}
        onBulkResultChange={onEmergencyBulkResultChange}
        onBulkResultResetRegister={emergencyBulkResultResetRef}
        onEmergencyReplacementPeriodModeChange={
          handleEmergencyReplacementPeriodModeChange
        }
      />

      {/* 마감 물어보기 */}
      <div className="flex mb-4">
        <div className="buttonGroup_leftSide">
          <div className="create_date_listButton" ref={refDateList}>
            <button
              type="button"
              className="changeDate changeDate--prev"
              onClick={() => {
                changeDate(-1);
              }}
              disabled={isChangeDateButtonClick || isWantedAdjustmentLoading}
            ></button>

            <button
              type="button"
              // className="create_date_list_wrap"
              className="min-w-[100px] flex text-sm! py-4.5 font-semibold"
              onClick={() => {
                setIsOpendChangeDateList((prev) => !prev);
              }}
              disabled={isChangeDateButtonClick || isWantedAdjustmentLoading}
            >
              {curDate?.year}년 {curDate?.month}월
            </button>

            {isOpendChangeDateList && (
              <div className="create_date_list" ref={refDateListScroll}>
                {dateListOptions.map((option) => (
                  <button
                    key={`${option.year}-${option.month}`}
                    type="button"
                    className={`create_date_selectBox ${
                      curDate?.year === option.year &&
                      curDate?.month === option.month
                        ? "current"
                        : ""
                    }`}
                    onClick={() => {
                      setIsOpendChangeDateList(false);
                      // 날짜만 변경 (useRosterVersions가 자동으로 버전 리스트를 가져옴)
                      setCurDate({ year: option.year, month: option.month });
                    }}
                    disabled={isWantedAdjustmentLoading}
                  >
                    {option.year}년 {option.month}월
                  </button>
                ))}
              </div>
            )}
            <button
              type="button"
              className="changeDate changeDate--next"
              onClick={() => {
                changeDate(1);
              }}
              disabled={isChangeDateButtonClick || isWantedAdjustmentLoading}
            ></button>
          </div>

          {/* 버전리스트 버튼 */}
          <ButtonRosterVersion
            selectedGroupId={selectedGroupId ?? null}
            hasWanted={hasWanted}
            isLoadingWantedStatus={isLoadingWantedStatus}
          />
        </div>

        <div className="flex gap-2 items-center bg-white ml-auto">
          {!isAdmin && (
            <>
              <div className="flex items-center gap-0">
                {/* 빈근무표 생성 */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      onClick={handleCreateEmptyRoster}
                      disabled={isWantedAdjustmentLoading}
                      className={cn(
                        "!gap-[10px]",
                        "transition-all",
                        "group size-10",
                        isWantedAdjustmentLoading &&
                          "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <img
                        src={images.icon_plus_btn}
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          "pointer-events-none flex-shrink-0",
                          "transition-all",
                          "brightness-90 group-hover:brightness-50"
                        )}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="bg-[#444444]"
                    arrowClassName="fill-[#444444]"
                    sideOffset={4}
                  >
                    <p>빈 근무표 생성</p>
                  </TooltipContent>
                </Tooltip>

                {/* 근무표 복사 */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      disabled={isRosterEmpty || isWantedAdjustmentLoading}
                      onClick={handleCopyRoster}
                      className={cn(
                        "!gap-[10px] size-10",
                        "transition-all",
                        (isRosterEmpty || isWantedAdjustmentLoading) &&
                          "opacity-50 cursor-not-allowed",
                        "group"
                      )}
                    >
                      <img
                        src={images.icon_copy}
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          "pointer-events-none flex-shrink-0",
                          "transition-all",
                          "brightness-90 group-hover:brightness-50",
                          isRosterEmpty && "group-hover:brightness-90"
                        )}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="bg-[#444444]"
                    arrowClassName="fill-[#444444]"
                    sideOffset={4}
                  >
                    <p>근무표 복사</p>
                  </TooltipContent>
                </Tooltip>

                {/* 엑셀 다운로드 */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      disabled={
                        !hasWanted ||
                        !curScheduleInfo?.scheduleId ||
                        isWantedAdjustmentLoading
                      }
                      onClick={scheduleDownload_excel}
                      className={cn(
                        "group !gap-[10px] size-8.5 flex items-center justify-center rounded-full border border-zinc-300",
                        "transition-colors transition-[filter]",
                        /* border: group-hover(X) — 버튼 본인 호버는 hover: */
                        "enabled:hover:border-zinc-500",
                        (!hasWanted ||
                          !curScheduleInfo?.scheduleId ||
                          isWantedAdjustmentLoading) &&
                          "cursor-not-allowed opacity-50",
                        !hasWanted &&
                          "btn_warningMessage btn_warningMessage--notRequestedWanted relative"
                      )}
                    >
                      <img
                        src="/asset/icon/config/download.svg"
                        alt=""
                        aria-hidden="true"
                        className={cn(
                          "pointer-events-none h-[1em] w-auto flex-shrink-0 ",
                          "transition-all",
                          "brightness-90 group-hover:brightness-50",
                          (!hasWanted ||
                            !curScheduleInfo?.scheduleId ||
                            isWantedAdjustmentLoading) &&
                            "group-hover:brightness-90"
                        )}
                      />
                    </button>
                  </TooltipTrigger>
                  <TooltipContent
                    className="bg-[#444444]"
                    arrowClassName="fill-[#444444]"
                    sideOffset={4}
                  >
                    <p>근무표 다운로드</p>
                  </TooltipContent>
                </Tooltip>
              </div>
              {isHeadNurse && (
                <div className="flex gap-1 items-center">
                  <ButtonInitial
                    ref={editModeButtonRef}
                    onClick={
                      openEditModeFromButton ??
                      (setIsEditMode
                        ? () => setIsEditMode((prev) => !prev)
                        : () => setUiMode(uiMode === "edit" ? "none" : "edit"))
                    }
                    disabled={
                      currentTeamTableViewTab === 1 ||
                      !curScheduleInfo?.scheduleId ||
                      isWantedAdjustmentLoading ||
                      isPublished
                    }
                    teamDisabled={currentTeamTableViewTab === 1}
                  >
                    수정
                  </ButtonInitial>

                  <ButtonInitial
                    disabled={
                      !curScheduleInfo?.scheduleId ||
                      isWantedAdjustmentLoading ||
                      isPublished
                    }
                    onClick={() => setConfirmMOdal(true)}
                  >
                    저장
                  </ButtonInitial>

                  <ButtonInitial
                    onClick={() => {
                      setDeleteModal(true);
                    }}
                    disabled={
                      isDeleting ||
                      !curScheduleInfo?.scheduleId ||
                      isWantedAdjustmentLoading ||
                      isPublished
                    }
                  >
                    삭제
                  </ButtonInitial>
                </div>
              )}
              <Tooltip>
                <TooltipTrigger asChild>
                  <span tabIndex={hasUnsavedChanges ? 0 : undefined}>
                    <ButtonInitial
                      ref={refEmergencyReplacementButton}
                      onClick={
                        openEmergencyModalFromButton ??
                        (() => {
                          setEmergencySlotPick(null);
                          setEmergencyOpen(true);
                        })
                      }
                      disabled={
                        !hasWanted ||
                        !curScheduleInfo?.scheduleId ||
                        isWantedAdjustmentLoading ||
                        isPublished ||
                        hasUnsavedChanges
                      }
                      className={cn(
                        "rounded-full gap-1.5 size-12! min-w-0! p-0!",
                        !hasWanted &&
                          "btn_warningMessage btn_warningMessage--notRequestedWanted relative",
                        isEmergencyOpen &&
                          "!bg-[#C1272D] !border-[#C1272D] hover:!bg-[#C1272D] hover:!border-[#C1272D] [&_img]:brightness-0 [&_img]:invert"
                      )}
                    >
                      <img
                        src="/asset/icon/config/emergency.svg"
                        alt=""
                        aria-hidden
                        className="size-5.5 shrink-0 p-0!"
                      />
                    </ButtonInitial>
                  </span>
                </TooltipTrigger>
                {hasUnsavedChanges && (
                  <TooltipContent
                    side="bottom"
                    className="max-w-[200px] text-center text-xs leading-relaxed"
                  >
                    수정 사항이 있습니다.
                    <br />
                    근무표를 저장한 후 이용해 주세요.
                  </TooltipContent>
                )}
              </Tooltip>
              <ButtonInitial
                onClick={() => {
                  setIsOpendWantedSelectorModal?.(true);
                  if (isOpendWantedSelectorModal === undefined)
                    setUiMode("wantedSelector");
                }}
                disabled={isWantedAdjustmentLoading || isPublished}
                className="min-w-[80px] py-3"
              >
                원티드
              </ButtonInitial>
            </>
          )}
          {isHeadNurse && (
            <>
              <BtnCreateRoster
                onClick={async () => {
                  if (copyNurse.length < 4) {
                    toast.error(
                      "4명이하 인원은 AI 근무표 생성에 적합하지 않습니다."
                    );
                    return;
                  }
                  setModalOpen(true);
                }}
                disabled={
                  isAdmin ||
                  isRosterCreating ||
                  !hasWanted ||
                  isWantedAdjustmentLoading
                }
                isCreating={isRosterCreating}
                pageErrorCase={hasWanted ? "prepared" : "notRequestedWanted"}
              />

              <Button
                className={cn(
                  "py-6 w-[100px] rounded-full bg-sig hover:bg-sig/80 ",
                  isPublished && "bg-accent-foreground/30"
                )}
                disabled={!hasWanted || isWantedAdjustmentLoading}
                onClick={() => {
                  setPublishModalAction(isPublished ? "unpublish" : "publish");
                  setPublishModal(true);
                }}
              >
                {isPublished ? "마감 철회" : "마감"}
              </Button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
